import React from 'react';
import { BarChart3, TrendingUp, TrendingDown } from 'lucide-react';

export default function InventoryTurnoverChart({ data }) {
  if (!data || data?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Taux de Rotation des Stocks</h3>
        <div className="text-center py-8 text-gray-500">
          <BarChart3 size={48} className="mx-auto mb-2 text-gray-300" />
          <p>Aucune donnée disponible</p>
        </div>
      </div>
    );
  }

  const maxValue = Math.max(...data?.map(d => d?.totalTurnover));
  const avgTurnover = data?.reduce((sum, d) => sum + d?.totalTurnover, 0) / data?.length;
  const lastWeek = data?.[data?.length - 1];
  const previousWeek = data?.[data?.length - 2];
  const trend = previousWeek ? ((lastWeek?.totalTurnover - previousWeek?.totalTurnover) / previousWeek?.totalTurnover) * 100 : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Taux de Rotation des Stocks</h3>
        <div className="flex items-center gap-2">
          {trend >= 0 ? (
            <TrendingUp size={18} className="text-green-600" />
          ) : (
            <TrendingDown size={18} className="text-red-600" />
          )}
          <span className={`text-sm font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? '+' : ''}{trend?.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="space-y-3 mb-4">
        {data?.map((item, index) => (
          <div key={index} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{item?.date}</span>
              <span className="font-medium text-gray-900">{item?.totalTurnover} unités</span>
            </div>
            <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
              {/* Noir 270L */}
              <div 
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-gray-700 to-gray-600 transition-all"
                style={{ width: `${(item?.noirSold / maxValue) * 100}%` }}
              >
                {item?.noirSold > 0 && (
                  <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-white font-medium">
                    {item?.noirSold}
                  </span>
                )}
              </div>
              {/* Bleu 220L */}
              <div 
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all"
                style={{ 
                  width: `${(item?.bleuSold / maxValue) * 100}%`,
                  marginLeft: `${(item?.noirSold / maxValue) * 100}%`
                }}
              >
                {item?.bleuSold > 0 && (
                  <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-white font-medium">
                    {item?.bleuSold}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-700 rounded"></div>
            <span className="text-xs text-gray-600">Noir 270L</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className="text-xs text-gray-600">Bleu 220L</span>
          </div>
        </div>
        <div className="text-xs text-gray-600">
          Moyenne: <span className="font-medium text-gray-900">{avgTurnover?.toFixed(1)}</span>
        </div>
      </div>
    </div>
  );
}